/**
 * rtProtocolGateModule.js — субмиллиметровый шлюз качества МРТ в приложении.
 *
 * Ядро (rtProtocolGateCore) проверено тестами (13/13): теги → настоящий воксель
 * (по матрице СЪЁМКИ, не рекона) → вердикт, ловит интерполяцию «1024 из 256».
 * Здесь — чтение тегов из кэша, КРАСНЫЙ баннер при несоответствии и печать
 * заключения о несоответствии.
 *
 * Источник тегов — то, что РЕАЛЬНО на экране: тома Cornerstone (cornerstone.cache),
 * плюс оба загрузчика (основной _seriesMap И воркер-мост). Раньше смотрели только
 * в getAllSeries() — и не видели серий, открытых через воркер (папка/DICOMDIR):
 * отсюда ложное «нет загруженной серии» при видимой на экране МРТ.
 */

import { log } from '../utils/logger.js';
// [11.1.3] showToast — чтобы кнопка «Качество КТ» отвечала при отказе, а не
// молчала: молчащая кнопка неотличима от мёртвой (урок синхронизации окон).
import { showToast } from '../utils/helpers.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { cornerstone, getRenderingEngine } from '../core/viewportState.js';
import { getAllSeries, getCachedDataSet, cacheDataSet } from '../loaders/loaderManager.js';
import { getWorkerSeries } from '../loaders/loaderWorkerBridge.js';
import { dicomImageLoader } from '../core/init.js';
import { evaluateSubMm, buildQualityReportHTML } from './rtProtocolGateCore.js';
import { orderGroups, pickSeries } from './rtSeriesPick.js';
import { openPrintWindow } from '../ui/printUtil.js';
// [12.0.23] Единый отчёт о качестве КТ и МРТ во вкладке правого сайдбара
import { отчётИзМРТ } from '../ui/qualityReportCore.js';
import { показатьКачество, переключитьКачество } from '../ui/qualityTab.js';
import { helpIcon } from './rtKnowledgeModule.js';   // [12.0.17] значок «?» у заголовка

// [11.1.4] autoCheck ВЫКЛЮЧЕНА по умолчанию — по решению владельца.
//
// ПОВОД. При загрузке серии шлюз сам считал вердикт и выбрасывал во всю ширину
// экрана оранжевый баннер «Почти субмиллиметр — есть замечания» с кнопкой
// печати. Врач открывает исследование, чтобы СМОТРЕТЬ СНИМОК, а получает
// поверх него окно, которого не просил, — и закрывает его при каждой загрузке.
//
// Проверка никуда не делась: кнопка «Качество МРТ» в верхней панели и пункт
// шлюза зовут checkSeries принудительно. Разница в том, кто решает, когда
// смотреть вердикт: теперь врач, а не программа.
let _cfg = { thresholdMm: 1.0, expectName: '', autoCheck: false };  // порог, заказанный протокол, автопроверка
let _checked = new Set();                          // уже проверенные тома
let _last = null;                                  // последний вердикт (+теги) для печати

// [10.0.1] Порог «мобильной вёрстки» — тот же, что в CSS (@media max-width:768px)
// и в mobileNav/mobileTopbar. Держим одно число на весь проект, иначе поведение
// JS и вёрстки разъедется на планшетах.
const MOBILE_BREAKPOINT_PX = 768;

function _isMobileView() {
  try {
    if (window.matchMedia) {
      return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
    }
    return window.innerWidth <= MOBILE_BREAKPOINT_PX;
  } catch { return false; }
}

function _readTags(ds) {
  if (!ds) return null;
  const fs = t => { try { return parseFloat(ds.floatString(t)); } catch (e) { return undefined; } };
  const st = t => { try { return ds.string(t) || ''; } catch (e) { return ''; } };
  const us = (t, i) => { try { return ds.uint16(t, i); } catch (e) { return 0; } };
  const ps = (st('x00280030') || '').split('\\').map(parseFloat).filter(Number.isFinite);
  return {
    sliceThickness: fs('x00180050'),
    spacingBetweenSlices: fs('x00180088'),
    rows: (() => { try { return ds.uint16('x00280010'); } catch (e) { return 0; } })(),
    columns: (() => { try { return ds.uint16('x00280011'); } catch (e) { return 0; } })(),
    pixelSpacing: ps.length === 2 ? ps : [ps[0] || 0, ps[0] || 0],
    acquisitionMatrix: [us('x00181310', 0), us('x00181310', 1), us('x00181310', 2), us('x00181310', 3)],
    mrAcquisitionType: st('x00180023'),
    seriesDescription: st('x0008103e'),
    protocolName: st('x00181030'),
    reconstructionDiameter: fs('x00181100'),
    modality: st('x00080060'),
    patientName: st('x00100010'),
    patientId: st('x00100020'),
    studyDate: st('x00080020'),
    studyDesc: st('x00081030'),
  };
}

const _baseId = (x) => String(x).split('?frame=')[0];

/**
 * Собрать кандидатов ПО СЕРИЯМ (а не одной кучей). Источники прежние: тома
 * Cornerstone (то, что реально на экране) плюс обе карты загрузчиков — серия,
 * открытая через воркер, в _seriesMap не попадает, зато её том в кэше есть.
 *
 * [7.0.17] Раньше все imageId сваливались в один плоский список, и дальше брался
 * тот, что оказался в СЕРЕДИНЕ. Пока открыта одна серия — работает. Но этап 3
 * (совмещение) требует держать КТ и МРТ открытыми ОДНОВРЕМЕННО, и тогда середина
 * общего списка попадает в ту серию, которая длиннее. Замерено на копии алгоритма:
 *
 *     КТ 200 + МРТ 332            -> взял МРТ (повезло)
 *     КТ 400 + МРТ 160            -> взял КТ
 *     КТ 512 + МРТ 176            -> взял КТ
 *     T1 176 + T2 176 + BRAVO 332 -> взял T2, хотя смотрят на BRAVO
 *
 * Планировочная КТ на 400-512 срезов — обычное дело, так что кнопка «Проверка
 * качества МРТ» показывала бы вердикт, посчитанный по КТ. И заметить это было
 * нельзя: имени серии в баннере не было.
 *
 * Теперь кандидаты сгруппированы по сериям, впереди — показанные в окнах, а
 * окончательный выбор идёт по модальности (см. _acquireDataSet).
 */
function _collectGroups() {
  const groups = [];
  const seenKey = new Set();
  const add = (key, ids, source) => {
    const list = [...new Set((ids || []).map(_baseId).filter(Boolean))];
    if (!list.length) return;
    const k = String(key || list[0]);
    if (seenKey.has(k)) return;
    seenKey.add(k);
    groups.push({ key: k, ids: list, source });
  };

  try {
    for (const v of (cornerstone?.cache?.getVolumes?.() || [])) add(v.volumeId || v.id, v.imageIds, 'volume');
  } catch (e) { /* */ }
  try {
    for (const s of (getAllSeries() || [])) add(s.seriesUID || s.seriesInstanceUID, (s.images || []).map(im => im?.imageId), 'loader');
  } catch (e) { /* */ }
  try {
    for (const s of (getWorkerSeries?.() || [])) add(s.seriesUID || s.seriesInstanceUID, (s.images || []).map(im => im?.imageId), 'worker');
  } catch (e) { /* */ }

  // Вперёд — тома, привязанные к текущим окнам: если открыто несколько МР-серий,
  // судить надо о той, на которую человек смотрит. Это только переупорядочивание,
  // на состав списка оно не влияет, поэтому при любой осечке ничего не меняется.
  try {
    const shown = new Set();
    for (const vp of (getRenderingEngine()?.getViewports?.() || [])) {
      for (const a of (vp.getActors?.() || [])) {
        const id = a?.uid || a?.referenceId || a?.volumeId;
        if (id) shown.add(String(id));
      }
    }
    return orderGroups(groups, shown);
  } catch (e) { return groups; }
}

/** Плоский список всех кандидатов (для диагностики и совместимости). */
function _collectImageIds() {
  return [...new Set(_collectGroups().flatMap(g => g.ids))];
}

/** dataSet ЧИТАЕМ? [7.0.18] В 7.0.16 нули на экране пошли оттого, что брался
 *  первый попавшийся объект: у Cornerstone `loadAndCacheImage().data` бывает с
 *  полем `.elements`, но это НЕ dicom-parser dataSet — методы floatString/string/
 *  uint16 отдают пустоту. Теперь принимаем объект, только если реально читается
 *  хотя бы один универсальный тег (Rows/Modality/SliceThickness/PixelSpacing). */
function _dsReadable(ds) {
  if (!ds) return false;
  try {
    const rows = ds.uint16 ? (ds.uint16('x00280010') || 0) : 0;
    const mod = ds.string ? (ds.string('x00080060') || '') : '';
    const thick = ds.floatString ? parseFloat(ds.floatString('x00180050')) : NaN;
    const ps = ds.string ? (ds.string('x00280030') || '') : '';
    return (rows > 0) || (mod !== '') || (Number.isFinite(thick) && thick > 0) || (ps !== '');
  } catch (e) { return false; }
}

/** Догрузить сырой dataSet из файла (машинерия панели тегов getDICOMTags).
 *  [7.0.18] fileManager отдаёт id вида `dicomfile:N`; прежний код слепо клеил
 *  `wadouri:` спереди → `wadouri:dicomfile:N`, и правильный разбор через
 *  dataSetCacheManager падал, а код сваливался в пустой объект. Пробуем ОБЕ формы
 *  и каждый результат валидируем _dsReadable — пустышки отбрасываются. */
async function _reloadDataSet(baseId) {
  const dscm = dicomImageLoader?.wadouri?.dataSetCacheManager;
  const forms = baseId.startsWith('wadouri:') ? [baseId] : [baseId, 'wadouri:' + baseId];
  for (const id of forms) {
    try {
      if (dscm?.load) {
        const loaded = await dscm.load(id, dicomImageLoader.wadouri.loadFileRequest || undefined);
        if (_dsReadable(loaded)) { try { cacheDataSet(baseId, loaded); } catch (e) {} return loaded; }
      }
    } catch (e) { /* следующая форма */ }
  }
  try {
    const image = await cornerstone.imageLoader.loadAndCacheImage(baseId);
    const ds = image?.data;
    if (_dsReadable(ds)) { try { cacheDataSet(baseId, ds); } catch (e) {} return ds; }
  } catch (e) { /* */ }
  return null;
}

/** Прочитать dataSet ОДНОЙ серии: от середины к краям (середина обычно ещё в
 *  LRU-кэше), при подрезанном кэше — догрузить несколько кандидатов.
 *  [7.0.18] Каждый кандидат из кэша тоже валидируется — пустой не принимаем,
 *  ищем следующий; иначе на экран уходили нули. */
async function _dataSetOfGroup(g) {
  const ids = g.ids;
  const order = ids.map((_, i) => i).sort((a, b) => Math.abs(a - ids.length / 2) - Math.abs(b - ids.length / 2));
  for (const i of order) { const ds = getCachedDataSet(ids[i]); if (_dsReadable(ds)) return ds; }
  for (const i of order.slice(0, 8)) { const ds = await _reloadDataSet(ids[i]); if (ds) return ds; }
  return null;
}

/**
 * Найти серию, о которой имеет смысл выносить вердикт.
 * Возвращает {none} — ничего не загружено; {noTags} — серия есть, но теги не
 * читаются; {ds, tags} — есть с чем работать; при отсутствии МР-серии добавляет
 * onlyModality, чтобы сказать об этом вслух, а не выдать КТ за МРТ.
 */
async function _acquireDataSet() {
  const groups = _collectGroups();
  if (!groups.length) return { none: true };
  // Само правило выбора — в rtSeriesPick.js, отдельно и под тестом.
  const r = await pickSeries(groups, async (g) => {
    const ds = await _dataSetOfGroup(g);
    if (!ds) return null;
    g._ds = ds;                       // чтобы не читать дважды
    return _readTags(ds);
  }, 'MR');
  if (r.none || r.noTags) return r;
  return { ds: r.picked._ds, tags: r.tags, onlyModality: r.onlyModality, groups: groups.length };
}

/**
 * [10.17.0] Вердикт шлюза по КОНКРЕТНОЙ серии, без баннера.
 *
 * Нужен режиму сравнения: там два исследования, и общий баннер поверх экрана
 * негоден трижды — вердикт один на два окна, непонятно, к какому относится, и
 * он закрывает сами снимки. Вердикт должен быть свой у каждого окна и стоять
 * в этом окне, поэтому здесь только расчёт, а показ — дело зовущего.
 */
export async function evaluateSeries(seriesUID) {
  if (!seriesUID) return null;
  // [10.19.1] Точное совпадение вместо подстроки: UID, являющийся префиксом
  // другого («…1.4.5» и «…1.4.51»), отдавал чужую серию в строку шлюза.
  const искомый = String(seriesUID);
  const g = _collectGroups().find((x) => String(x.key) === искомый)
    || _collectGroups().find((x) => String(x.key).endsWith(`:${искомый}`) || String(x.key).startsWith(`${искомый}:`));
  if (!g) return null;
  const ds = await _dataSetOfGroup(g);
  if (!ds) return null;
  const tags = _readTags(ds);
  if (!tags || String(tags.modality).toUpperCase() !== 'MR') return null;
  return { res: evaluateSubMm(tags, { thresholdMm: _cfg.thresholdMm, expectName: _cfg.expectName || undefined }), tags };
}

/** Проверить текущую (последнюю) серию; при несоответствии показать баннер. */
export async function checkSeries(opts = {}) {
  const found = await _acquireDataSet();
  if (found.none) {
    log.warn('[GATE] Нет загруженной серии (ни тома Cornerstone, ни серии загрузчиков).');
    if (opts.force) _chip('🔬 Нет загруженной серии — сначала откройте МР-исследование', '#b26a00');
    return null;
  }
  if (found.noTags) {
    // Серия ЕСТЬ на экране, но теги недоступны — это НЕ «нет серии». Честное сообщение.
    log.warn('[GATE] Серия загружена, но DICOM-теги недоступны для чтения.');
    if (opts.force) _chip('🛡 Серия открыта, но DICOM-теги недоступны — переоткройте исследование и повторите', '#b26a00');
    return null;
  }
  const tags = found.tags || _readTags(found.ds);
  if (String(tags.modality).toUpperCase() !== 'MR') {
    // Авто-режим: для КТ субмиллиметровый шлюз не нужен, молчим.
    if (!opts.force) return null;
    // По кнопке: МР-серии среди открытых нет. Говорим об этом прямо, а не
    // выдаём цифры КТ за проверку качества МРТ.
    log.warn(`[GATE] Среди открытых серий нет МР — найдена только ${found.onlyModality || tags.modality || '—'}.`);
    _chip(`🔬 Открыта серия ${found.onlyModality || tags.modality || '—'}, а не МР — субмиллиметровый шлюз проверяет МРТ`, '#b26a00');
    return null;
  }
  const res = evaluateSubMm(tags, { thresholdMm: _cfg.thresholdMm, expectName: _cfg.expectName || undefined });
  _last = { res, tags };
  // [11.1.5] Баннер — ТОЛЬКО для автопроверки, не для кнопки.
  //
  // ПОВОД: владелец прислал снимок «картинка на картинке» — окно вердикта
  // поверх баннера того же вердикта. Один щелчок по «Качество МРТ» открывал
  // ДВА окна: checkSeries рисовал баннер во всю ширину, а showQualityReport
  // поверх него — подробное модальное окно. Два сообщения об одном и том же,
  // причём верхнее закрывало нижнее.
  //
  // Роли разведены: нажал кнопку — получаешь ОКНО с таблицей и печатью;
  // сработала автопроверка (она сейчас выключена по умолчанию) — баннер.
  if (!opts.тихо) _banner(res, tags);
  log.info(`[GATE] ${tags.seriesDescription || '—'}: ${res.level.toUpperCase()} · срез ${res.throughMm}мм · воксель ${res.trueInPlaneMm}мм`
    + (res.interpolated ? ' · интерполяция' : ''));
  return { res, tags };
}

/** Небольшая всплывающая плашка (зелёная — при соответствии), сама исчезает. */
function _chip(text, bg) {
  document.getElementById('subm-chip')?.remove();
  const c = document.createElement('div');
  c.id = 'subm-chip';
  c.style.cssText = 'position:fixed;left:50%;top:10px;transform:translateX(-50%);z-index:99999;'
    + `background:${bg};color:#fff;padding:8px 16px;border-radius:999px;font:13px/1.4 sans-serif;`
    + 'box-shadow:0 4px 18px rgba(0,0,0,.4);border:1px solid rgba(255,255,255,.35)';
  c.textContent = text;
  document.body.appendChild(c);
  setTimeout(() => c.remove(), 6000);
}

// ── баннер вердикта (никогда не молчит) ──────────────────────────────────────
function _banner(res, tags) {
  document.getElementById('subm-banner')?.remove();
  document.getElementById('subm-chip')?.remove();
  // [10.17.0] В режиме сравнения общий баннер молчит: он один на два
  // исследования, не говорит, к какому относится, и закрывает собой оба окна.
  // Там вердикт показывается в каждом окне отдельно (comparePanel).
  try {
    if (typeof document !== 'undefined' && document.body?.classList?.contains('compare-mode')) return;
  } catch (e) { /* нет DOM — значит и баннера нет */ }
  if (res.level === 'ok') {
    // Раньше при «ок» баннера не было вовсе — и врач не понимал, прошла ли проверка.
    // Теперь коротко подтверждаем: субмиллиметр в порядке.
    // [7.0.17] Имя серии — в плашке: при нескольких открытых сериях иначе не
    // понять, о какой из них вердикт.
    _chip(`✓ ${res.sequence} — субмиллиметр подтверждён · срез ${res.throughMm} мм · воксель ${res.trueInPlaneMm} мм`, '#127a2e');
    return;
  }
  const fail = res.level === 'fail';
  const bg = fail ? '#c0161c' : '#b26a00';
  const icon = fail ? '⛔' : '⚠️';
  const head = fail ? 'СКАН НЕ СУБМИЛЛИМЕТРОВЫЙ — не для точного планирования'
    : 'Почти субмиллиметр — есть замечания (для 3D обычно приемлемо)';
  const b = document.createElement('div');
  b.id = 'subm-banner';
  b.style.cssText = 'position:fixed;left:50%;top:0;transform:translateX(-50%);z-index:99999;'
    + `background:${bg};color:#fff;max-width:760px;width:calc(100% - 24px);margin-top:8px;`
    + 'border-radius:10px;box-shadow:0 6px 30px rgba(0,0,0,.5);font:13px/1.5 sans-serif;'
    + 'padding:12px 16px;border:2px solid rgba(255,255,255,.35)';
  const rows = res.reasons.map(r => `<li>${_esc(r)}</li>`).join('');
  b.innerHTML = `
    <div style="display:flex;align-items:center;gap:10px">
      <div style="font-size:26px">${icon}</div>
      <div style="flex:1">
        <div style="font-weight:800;font-size:15px;letter-spacing:.2px">${icon} ${head}</div>
        <div style="opacity:.95;margin-top:2px">
          <!-- [7.0.17] Имя серии — первым: иначе при открытых КТ+МРТ не видно,
               о какой серии вердикт. -->
          Серия <b>${_esc(res.sequence)}</b> (${_esc(res.modality)})<br>
          Срез <b>${res.throughMm} мм</b> · реальный воксель в плоскости <b>${res.trueInPlaneMm} мм</b>
          · съёмка ${res.acqMatrix[0]}×${res.acqMatrix[1]} · рекон ${res.reconMatrix[0]}×${res.reconMatrix[1]}
          · ${res.mrType} · порог ${res.thresholdMm} мм
        </div>
      </div>
      <button id="subm-print" style="background:#fff;color:#111;border:0;border-radius:6px;padding:7px 12px;font-weight:700;cursor:pointer">🖨 Печать заключения</button>
      <button id="subm-close" style="background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5);border-radius:6px;padding:7px 10px;cursor:pointer">✕</button>
    </div>
    <ul style="margin:8px 0 0 30px;padding:0">${rows}</ul>`;
  document.body.appendChild(b);
  b.querySelector('#subm-close').onclick = () => b.remove();
  b.querySelector('#subm-print').onclick = () => printReport();
}

// ── печать заключения о несоответствии ───────────────────────────────────────
// [7.0.23] Содержание документа — buildQualityReportHTML (ядро, под тестами:
// вердикты по уровням, цифры, экранирование, авторство). Механика окна —
// printUtil.openPrintWindow (без импортов, проверен под заглушкой DOM, с
// fallback в iframe при блокировщике всплывающих).
export function printReport() {
  if (!_last) { log.warn('[GATE] Нет вердикта для печати — сначала проверьте серию.'); return; }
  const html = buildQualityReportHTML(_last.res, _last.tags);
  const via = openPrintWindow(html, window);
  if (via === 'none') log.warn('[GATE] Не удалось открыть окно печати (и iframe не сработал).');
  else log.info('[GATE] Печать заключения: путь ' + via);
}

// ── настройки и автозапуск ───────────────────────────────────────────────────
export function setGateConfig(cfg = {}) {
  if (cfg.thresholdMm != null) _cfg.thresholdMm = +cfg.thresholdMm || 1.0;
  if (cfg.expectName != null) _cfg.expectName = String(cfg.expectName);
  // [10.0.1] Ручной выключатель автопроверки — на случай, если баннер мешает
  // и на десктопе (демонстрация, запись экрана, работа с заведомо не-МР сериями)
  if (cfg.autoCheck != null) _cfg.autoCheck = !!cfg.autoCheck;
  return { ..._cfg };
}

/** Запускается ли автопроверка сейчас. Полезно для диагностики. */
export function isAutoCheckActive() {
  return _cfg.autoCheck && !_isMobileView();
}

function _startWatch() {
  // без общей шины: следим за появлением нового тома и проверяем его
  setInterval(() => {
    try {
      const vols = cornerstone?.cache?.getVolumes?.() || [];

      // [10.0.1] На мобильных автопроверка не запускается.
      //
      // Вердикт показывается баннером во всю ширину экрана сверху, с крупной
      // кнопкой «Печать заключения». На десктопе это уместно — на телефоне
      // плашка вылезала поверх снимка сразу после загрузки срезов и закрывала
      // половину экрана, а печатать заключение с телефона всё равно никто не
      // будет. Ручная проверка (кнопка «Качество МРТ» в панели и пункт шлюза)
      // на мобильных работает как прежде — она вызывает checkSeries с force.
      //
      // Тома всё равно помечаем просмотренными: иначе при повороте экрана в
      // альбомную ориентацию (ширина > 768) разом всплыли бы баннеры по всем
      // сериям, загруженным в портретной.
      const skipAuto = !_cfg.autoCheck || _isMobileView();

      for (const v of vols) {
        const id = v.volumeId || v.id;
        if (id && !_checked.has(id) && (v.imageIds || []).length) {
          _checked.add(id);
          if (skipAuto) continue;
          setTimeout(() => checkSeries(), 800);   // дать тегам осесть в кэше
        }
      }
    } catch (e) { /* */ }
  }, 2000);
}

// ── UI-панель (порог, заказанный протокол, ручная проверка) ─────────────────
export function mountGateUI() {
  if (document.getElementById('gate-box')) return;
  const host = document.getElementById('rtstruct-panel') || document.body;
  const box = document.createElement('div');
  box.id = 'gate-box';
  box.className = 'rt-sec';
  box.innerHTML = `
    <details class="rt-fold" open>
      <summary class="rt-sec-head"><span class="rt-sec-title">Субмиллиметровый шлюз${helpIcon('slice-step')}</span></summary>
    <label class="rt-field"><span>Порог, мм</span>
      <input id="gate-thr" class="rt-input" type="number" step="0.1" value="1.0"></label>
    <label class="rt-field"><span>Заказанный протокол (для сверки)</span>
      <input id="gate-exp" class="rt-input" type="text" placeholder="напр. 3D T1 BRAVO"></label>
    <div class="rt-row">
      <button id="gate-run" class="rt-btn rt-btn--primary">Проверить серию</button>
      <button id="gate-print" class="rt-btn rt-btn--sm">Печать</button>
    </div>
    <div id="gate-out" class="rt-out"></div>
    </details>`;
  host.appendChild(box);
  box.querySelector('#gate-run').onclick = async () => {
    setGateConfig({ thresholdMm: box.querySelector('#gate-thr').value, expectName: box.querySelector('#gate-exp').value });
    const out = box.querySelector('#gate-out');
    out.textContent = 'Проверка…';
    const r = await checkSeries({ force: true });
    if (!r) { out.textContent = 'Серия не найдена или теги не читаются (см. плашку сверху).'; return; }
    const col = r.res.level === 'fail' ? 'var(--error)' : r.res.level === 'warn' ? 'var(--warning)' : 'var(--success)';
    out.innerHTML = `<b style="color:${col}">${r.res.level.toUpperCase()}</b> · срез ${r.res.throughMm}мм · воксель ${r.res.trueInPlaneMm}мм`;
  };
  box.querySelector('#gate-print').onclick = () => printReport();
}

// ── ВИДИМАЯ кнопка «Проверка качества МРТ» + заключение на экране ─────────────
// [12.0.23] _verdictStyle убрана: тексты вердиктов живут в ui/qualityReportCore.js
// (отчётИзМРТ) — одно место для экрана, без второй копии здесь.

/** Проверить текущую серию и показать заключение на экране (с кнопкой печати). */
export async function showQualityReport() {
  // [12.0.23] Повторное нажатие «Качество МРТ» закрывает отчёт — как и прежде
  // закрывалось окно, и как ведёт себя кнопка «Качество КТ».
  if (переключитьКачество('МРТ')) return;
  // checkSeries сам покажет точную плашку (нет серии / теги не читаются) при force.
  // [11.1.5] `тихо: true` — чтобы не всплыл баннер: окно ниже покажет то же
  // самое, только подробнее и с печатью.
  const r = await checkSeries({ force: true, тихо: true });
  if (r) _qualityModal(r.res, r.tags);
}

function _qualityModal(res, tags) {
  document.getElementById('mq-modal')?.remove();
  // [11.1.5] И баннер с меткой тоже: они могли остаться от автопроверки или от
  // прошлого нажатия, и окно легло бы поверх них — та самая «картинка на
  // картинке» со снимка владельца. Один вердикт — одно окно на экране.
  document.getElementById('subm-banner')?.remove();
  document.getElementById('subm-chip')?.remove();

  // [12.0.23] ОТЧЁТ ПЕРЕЕХАЛ ВО ВКЛАДКУ «КАЧЕСТВО» ПРАВОГО САЙДБАРА.
  //
  // Здесь было белое модальное окно #mq-modal посреди экрана, затемнявшее
  // снимки, — на тёмной программе и в другом строении, чем отчёт КТ. Теперь
  // оба отчёта строятся одной схемой (ui/qualityReportCore.js) и живут в одной
  // вкладке, где врач читает вердикт, не теряя серию из виду.
  //
  // Печать осталась прежней — buildQualityReportHTML: её содержание уже под
  // тестами (уровни, цифры, экранирование, авторство), и менять проверенный
  // документ ради единообразия незачем. Белый лист для бумаги и так верен.
  показатьКачество('МРТ', {
    варианты: [{ подпись: '', отчёт: отчётИзМРТ(res, tags) }],
    печать: () => printReport(),
    снова: async () => {
      const r = await checkSeries({ force: true, тихо: true });
      if (r) _qualityModal(r.res, r.tags);
    },
  });
}

/** [7.0.18] Кнопка проверки — в ВЕРХНЕЙ панели, рядом с «✅ Протокол» (просьба
 *  Андрея). Плавающая кнопка справа снизу убрана — загораживала экран. Стиль и
 *  класс берём у соседней кнопки, стрелку дропдауна глушим тем же приёмом, что
 *  у #btn-protocol-check. */
function _mountToolbarButton() {
  if (document.getElementById('btn-mrq-check')) return true;
  const anchor = document.getElementById('btn-protocol-check');
  if (!anchor || !anchor.parentNode) return false;
  const b = document.createElement('button');
  b.id = 'btn-mrq-check';
  b.className = anchor.className || 'tb-dropdown-trigger';
  b.style.flexShrink = '0';
  b.title = 'Проверка качества МРТ: субмиллиметровый шлюз — вердикт на экране и печать заключения';
  // [7.0.24] Иконка — SVG, как у всех кнопок панели (было эмодзи 🔬, выбивалось
  // из стиля). Лупа из спрайта приложения (#i-search): проверка/инспекция.
  b.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-search"/></svg> Качество МРТ';
  b.onclick = () => showQualityReport();
  anchor.parentNode.insertBefore(b, anchor.nextSibling);
  // [7.0.24] И сосед «✅ Протокол» — тоже на SVG (галочка тем же классом .ico:
  // stroke/currentColor из общего стиля). Идемпотентно: только если там эмодзи.
  try {
    if (/✅/.test(anchor.textContent || '')) {
      anchor.innerHTML = '<svg class="ico" aria-hidden="true" viewBox="0 0 20 20"><path d="M3.5 10.5l4.5 4.5L16.5 5.5"/></svg> Протокол';
    }
  } catch (e) { /* некритично */ }
  if (!document.getElementById('mrq-btn-css')) {
    const s = document.createElement('style');
    s.id = 'mrq-btn-css';
    s.textContent = '#btn-mrq-check::after{content:none} #btn-ctq-check::after{content:none}';
    document.head.appendChild(s);
  }
  _смонтироватьКнопкуКТ(b);
  return true;
}

/**
 * [11.1.3] Кнопка «Качество КТ» — рядом с «Качество МРТ». По просьбе владельца.
 *
 * ПОВОД. Анализ качества КТ в программе есть и подробный: kVp, mA, ядро
 * реконструкции, толщина среза, шум, а главное — «что можно оценить» и
 * «ограничения» (годится ли исследование для Agatston, ВРКТ, CPR). Но входа с
 * панели у него не было: окно открывалось только щелчком по значку качества в
 * строке состояния, а тот виден не всегда и на ноутбуке уступает место кнопкам.
 * Рядом при этом стояла кнопка «Качество МРТ» — получалась несимметрия:
 * у МРТ вход на виду, у КТ спрятан.
 *
 * ПОЧЕМУ КНОПКА СТРОИТСЯ КОДОМ, а не лежит в index.html: соседка «Качество
 * МРТ» тоже строится кодом (_mountToolbarButton выше), и класс/оформление
 * берутся у соседней кнопки. Если положить КТ в разметку, она встанет в другом
 * месте и другим стилем. Здесь важнее одинаковость пары, чем видимость в
 * разметке — но за это приходится платить: обычный поиск по index.html обеих
 * кнопок НЕ НАЙДЁТ. Я сам на этом ошибся, когда искал «Качество МРТ» и не
 * нашёл. Отсюда сторож в verify: обе кнопки обязаны быть видны в src/.
 */
function _смонтироватьКнопкуКТ(соседка) {
  if (document.getElementById('btn-ctq-check')) return;
  if (!соседка || !соседка.parentNode) return;
  const b = document.createElement('button');
  b.id = 'btn-ctq-check';
  b.className = соседка.className || 'tb-dropdown-trigger';
  // [11.1.8] БЕЗ flex-shrink:0, в отличие от соседки. Именно нерастяжимость
  // распёрла верхний ряд шире окна: страница получила горизонтальную
  // прокрутку, левый сайдбар уехал за край, и вместе с ним — свёрнутая секция
  // «Совмещение». Врач сказал «исчезла панель совмещения»; исчезла не панель,
  // уехал сайдбар, и виновата была эта строка.
  b.style.minWidth = '0';
  b.title = 'Анализ качества КТ: параметры съёмки, шум, что можно оценить и чего исследование не позволяет';
  b.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-check"/></svg> Качество КТ';
  b.onclick = async () => {
    try {
      const m = await import('../loaders/loaderManager.js');
      m.showStudyQuality();
    } catch (e) {
      log.warn('[Качество КТ]', e.message);
      showToast('Не удалось открыть анализ качества: ' + e.message, 'error', 4000);
    }
  };
  соседка.parentNode.insertBefore(b, соседка.nextSibling);
}

function _mountButton() {
  document.getElementById('mq-fab')?.remove();   // старую плавающую убираем, если вдруг осталась
  if (_mountToolbarButton()) return;
  // Панель ещё строится — дожимаем, как credits.js.
  let tries = 0;
  const t = setInterval(() => {
    if (_mountToolbarButton() || ++tries > 20) {
      clearInterval(t);
      if (tries > 20) log.warn('[GATE] Не нашёл верхнюю панель (#btn-protocol-check) — проверка доступна через панель шлюза и MikhmelViewer.showQualityReport().');
    }
  }, 500);
}

export function initProtocolGate() {
  try {
    mountGateUI();
    _mountButton();
    _startWatch();
    registerAPIs({ checkSeries, printReport, setGateConfig, showQualityReport });
    log.info('[GATE] Субмиллиметровый шлюз инициализирован — 1.6 (SVG-иконки кнопок панели; выбор МР-серии по модальности; валидация читаемости dataSet)');
  } catch (e) { log.error('[GATE] init failed:', e); }
}

function _esc(s) { return String(s).replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m])); }

export default { initProtocolGate, checkSeries, printReport, setGateConfig, showQualityReport, isAutoCheckActive };
